import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { selector as productSelector } from 'containers/product-initializer/product-module';
import { FavoriteItemDto, PlayerSearchSuggestionDto, SeasonEventDto, TeamByNameDto, TeamDto } from 'utils/api/dto';
import { useEliteProspectPhotos } from 'utils/hooks/use-elite-prospect-photos';
import { generateManageFavoritesLink } from 'widgets/favorites/constant';

import { MIN_INPUT_LENGTH } from '../top-search-panel/constants';
import EventSearchResult from './event-search-result';
import { useRecentSearchRedirect } from './hooks/use-recent-search-redirect';
import { useSearchFavorites } from './hooks/use-search-favorites';
import RecentPlayerSearchResult from './recent-players-search-result';
import RecentTeamSearchResult from './recent-team-search-result';
import FavoritesSection, { FavoritePlayer } from './search-dropdown/favorites-section';
import ResultsSection from './search-dropdown/results-section';
import { FavoriteToggleHandler } from './search-dropdown/types';

const EMPTY_PLAYERS: PlayerSearchSuggestionDto[] = [];
const EMPTY_TEAMS: TeamByNameDto[] = [];
const EMPTY_EVENTS: SeasonEventDto[] = [];

type SearchResultProps = {
    players?: PlayerSearchSuggestionDto[];
    teams?: TeamByNameDto[];
    events?: SeasonEventDto[];
    onTeamSelect?: (teamId: number) => void;
    onEventClick?: (eventId?: number) => void;
    /** the stars and the favorites block are shown */
    isFavorite?: boolean;
    /** a page search of one kind: a plain list of it */
    isOnlyRecentPlayer?: boolean;
    isOnlyRecentTeam?: boolean;
    isOnlyEvents?: boolean;
    favTeamList?: FavoriteItemDto[];
    favEventList?: { id: number }[];
    favPlayerList?: FavoriteItemDto[];
    onFavoriteToggle: FavoriteToggleHandler;
    /** the profiles of the favorite players; empty slots are skipped */
    allFavoritePlayers?: (FavoritePlayer | null | undefined)[];
    allTeams?: TeamDto[];
    processing: boolean;
    searchQuery?: string;
    searchFailed?: boolean;
};

/** The results of a search panel: the header dropdown (results and favorites) or a plain list of one kind */
export const SearchResult = ({
    players = EMPTY_PLAYERS,
    teams = EMPTY_TEAMS,
    events = EMPTY_EVENTS,
    onTeamSelect,
    onEventClick,
    isFavorite = false,
    isOnlyRecentPlayer = false,
    isOnlyRecentTeam = false,
    isOnlyEvents = false,
    favTeamList,
    favEventList,
    favPlayerList,
    onFavoriteToggle,
    allFavoritePlayers,
    allTeams,
    processing,
    searchQuery = '',
    searchFailed = false
}: SearchResultProps) => {
    const history = useHistory();
    const product = useSelector(productSelector);
    const handleSelect = useRecentSearchRedirect({ onTeamSelect, onEventClick });

    const favorites = useMemo(() => (allFavoritePlayers ?? []).filter((player): player is FavoritePlayer => !!player), [
        allFavoritePlayers
    ]);
    const { favoriteTeams, getFavoriteId } = useSearchFavorites({ favPlayerList, favTeamList, favorites, allTeams });

    const photoPlayers = useMemo(() => [...players, ...favorites], [players, favorites]);
    const { getPhotoUrl } = useEliteProspectPhotos({ players: photoPlayers });

    const handleViewAllFavorites = useCallback(() => {
        history.push(generateManageFavoritesLink(product));
    }, [history, product]);

    if (isOnlyRecentPlayer) {
        return (
            <RecentPlayerSearchResult
                processing={processing}
                players={players}
                favorites={favorites}
                isFavorite={isFavorite}
                handleAddRecentData={handleSelect}
                handleFavoriteToggle={onFavoriteToggle}
                getPhotoUrl={getPhotoUrl}
            />
        );
    }
    if (isOnlyRecentTeam) {
        return (
            <RecentTeamSearchResult
                processing={processing}
                teams={teams}
                isFavorite={isFavorite}
                favTeamList={favTeamList}
                handleAddRecentData={handleSelect}
                handleFavoriteToggle={onFavoriteToggle}
            />
        );
    }
    if (isOnlyEvents) {
        return (
            <EventSearchResult
                processing={processing}
                events={events}
                isFavorite={isFavorite}
                favEventList={favEventList}
                handleAddRecentData={handleSelect}
                handleFavoriteToggle={onFavoriteToggle}
            />
        );
    }

    const query = searchQuery.trim();

    return (
        <>
            {query.length >= MIN_INPUT_LENGTH ? (
                <ResultsSection
                    query={query}
                    players={players}
                    teams={teams}
                    processing={processing}
                    failed={searchFailed}
                    getPhotoUrl={getPhotoUrl}
                    getFavoriteId={isFavorite ? getFavoriteId : null}
                    onSelect={handleSelect}
                    onFavoriteToggle={onFavoriteToggle}
                />
            ) : null}
            {isFavorite ? (
                <FavoritesSection
                    players={favorites}
                    teams={favoriteTeams}
                    getPhotoUrl={getPhotoUrl}
                    onSelect={handleSelect}
                    onViewAll={handleViewAllFavorites}
                />
            ) : null}
        </>
    );
};
